// Génération et résolution de labyrinthe — fonctions pures, sans DOM.
// Le générateur aléatoire est injectable pour rendre les tests déterministes.

export const DIRECTIONS = Object.freeze({
  up:    { dx: 0,  dy: -1, wall: 'top',    opposite: 'bottom' },
  right: { dx: 1,  dy: 0,  wall: 'right',  opposite: 'left' },
  down:  { dx: 0,  dy: 1,  wall: 'bottom', opposite: 'top' },
  left:  { dx: -1, dy: 0,  wall: 'left',   opposite: 'right' },
});

export function createCell(x, y) {
  return {
    x,
    y,
    visited: false,
    walls: { top: true, right: true, bottom: true, left: true },
  };
}

export function createGrid(size) {
  const grid = [];
  for (let y = 0; y < size; y++) {
    const row = [];
    for (let x = 0; x < size; x++) row.push(createCell(x, y));
    grid.push(row);
  }
  return grid;
}

export function countWalls(cell) {
  const w = cell.walls;
  return (w.top ? 1 : 0) + (w.right ? 1 : 0) + (w.bottom ? 1 : 0) + (w.left ? 1 : 0);
}

function getUnvisitedNeighbors(grid, cell) {
  const size = grid.length;
  const neighbors = [];
  const { x, y } = cell;
  if (y > 0 && !grid[y - 1][x].visited) neighbors.push({ cell: grid[y - 1][x], wall: 'top', opposite: 'bottom' });
  if (x < size - 1 && !grid[y][x + 1].visited) neighbors.push({ cell: grid[y][x + 1], wall: 'right', opposite: 'left' });
  if (y < size - 1 && !grid[y + 1][x].visited) neighbors.push({ cell: grid[y + 1][x], wall: 'bottom', opposite: 'top' });
  if (x > 0 && !grid[y][x - 1].visited) neighbors.push({ cell: grid[y][x - 1], wall: 'left', opposite: 'right' });
  return neighbors;
}

// Tressage (braiding) : ouvre certains culs-de-sac pour créer des boucles
function braid(grid, keepDeadEndChance, random) {
  const size = grid.length;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const cell = grid[y][x];
      if (countWalls(cell) !== 3) continue; // un cul-de-sac a exactement 3 murs
      if (random() <= keepDeadEndChance) continue;

      const breakable = [];
      if (cell.walls.top && y > 0) breakable.push({ wall: 'top', opp: 'bottom', ny: y - 1, nx: x });
      if (cell.walls.bottom && y < size - 1) breakable.push({ wall: 'bottom', opp: 'top', ny: y + 1, nx: x });
      if (cell.walls.left && x > 0) breakable.push({ wall: 'left', opp: 'right', ny: y, nx: x - 1 });
      if (cell.walls.right && x < size - 1) breakable.push({ wall: 'right', opp: 'left', ny: y, nx: x + 1 });

      if (breakable.length > 0) {
        const chosen = breakable[Math.floor(random() * breakable.length)];
        cell.walls[chosen.wall] = false;
        grid[chosen.ny][chosen.nx].walls[chosen.opp] = false;
      }
    }
  }
}

/**
 * Génère un labyrinthe parfait (backtracking itératif) puis applique le tressage.
 * @param {number} size taille de la grille (size × size)
 * @param {object} [options]
 * @param {number} [options.twistiness=0.8] 0 = couloirs droits, 1 = très tortueux
 * @param {number} [options.deadEnds=1.0] ratio d'impasses conservées
 * @param {() => number} [options.random=Math.random] générateur dans [0, 1)
 */
export function generateMaze(size, { twistiness = 0.8, deadEnds = 1.0, random = Math.random } = {}) {
  const grid = createGrid(size);
  const stack = [];
  let current = grid[0][0];
  current.visited = true;
  stack.push(current);
  let currentDir = null; // direction du dernier mouvement, pour créer des couloirs

  while (stack.length > 0) {
    current = stack.pop();
    const unvisited = getUnvisitedNeighbors(grid, current);

    if (unvisited.length === 0) {
      currentDir = null; // on perd l'élan quand on recule
      continue;
    }

    stack.push(current);

    // Probabilité de continuer tout droit contrôlée par la twistiness
    const sameDirNeighbor = unvisited.find((n) => n.wall === currentDir);
    const chosen = sameDirNeighbor && random() > twistiness
      ? sameDirNeighbor
      : unvisited[Math.floor(random() * unvisited.length)];

    currentDir = chosen.wall;
    current.walls[chosen.wall] = false;
    chosen.cell.walls[chosen.opposite] = false;
    chosen.cell.visited = true;
    stack.push(chosen.cell);
  }

  if (deadEnds < 1.0) braid(grid, deadEnds, random);

  return grid;
}

/**
 * Plus court chemin (BFS) du coin haut-gauche au coin bas-droit.
 * @returns {{x:number,y:number}[]} chemin incluant départ et arrivée, [] si aucun
 */
export function findOptimalPath(grid) {
  const size = grid.length;
  const endX = size - 1;
  const endY = size - 1;
  const visited = Array.from({ length: size }, () => Array(size).fill(false));
  const parent = new Map();
  const key = (x, y) => y * size + x;

  const queue = [{ x: 0, y: 0 }];
  visited[0][0] = true;

  while (queue.length > 0) {
    const curr = queue.shift();
    if (curr.x === endX && curr.y === endY) {
      const path = [];
      let node = curr;
      while (node) {
        path.push({ x: node.x, y: node.y });
        node = parent.get(key(node.x, node.y));
      }
      return path.reverse();
    }

    const cell = grid[curr.y][curr.x];
    for (const dir of Object.values(DIRECTIONS)) {
      const nx = curr.x + dir.dx;
      const ny = curr.y + dir.dy;
      if (nx < 0 || nx >= size || ny < 0 || ny >= size) continue;
      if (cell.walls[dir.wall] || visited[ny][nx]) continue;
      visited[ny][nx] = true;
      parent.set(key(nx, ny), curr);
      queue.push({ x: nx, y: ny });
    }
  }
  return [];
}

/**
 * Tente de déplacer une position d'une case dans une direction.
 * @returns {{x:number,y:number}|null} nouvelle position, ou null si un mur bloque
 */
export function tryMove(grid, pos, direction) {
  const dir = DIRECTIONS[direction];
  if (!dir) return null;
  const cell = grid[pos.y]?.[pos.x];
  if (!cell || cell.walls[dir.wall]) return null;
  return { x: pos.x + dir.dx, y: pos.y + dir.dy };
}

/**
 * Cherche une ligne droite de cases contiguës, sans mur, de `from` vers `target`.
 * Sert au déplacement à la souris : on ne peut viser qu'une case alignée et visible.
 * @returns {{direction:string, steps:number}|null} null si non alignée, identique ou bloquée par un mur
 */
export function findStraightPath(grid, from, target) {
  const dx = target.x - from.x;
  const dy = target.y - from.y;
  if ((dx === 0) === (dy === 0)) return null; // même case, ou diagonale

  let direction;
  if (dx !== 0) direction = dx > 0 ? 'right' : 'left';
  else direction = dy > 0 ? 'down' : 'up';

  const steps = Math.abs(dx + dy);
  let pos = from;
  for (let i = 0; i < steps; i++) {
    pos = tryMove(grid, pos, direction);
    if (!pos) return null;
  }
  return { direction, steps };
}

const REVERSE = Object.freeze({ up: 'down', down: 'up', left: 'right', right: 'left' });

/**
 * Direction réellement prise pour un pas de glissement (voir core/swipe.js) : la direction
 * dominante si elle est libre, sinon la perpendiculaire vers laquelle penche le geste, sinon le
 * virage du couloir quand il n'y a qu'une issue hors demi-tour (le geste n'a pas besoin de suivre
 * chaque coude ; un vrai carrefour attend que le joueur choisisse).
 * @returns {string|null} direction prise, ou null si rien ne convient
 */
export function resolveSwipeStep(grid, pos, primary, secondary = null) {
  if (tryMove(grid, pos, primary)) return primary;
  if (secondary && tryMove(grid, pos, secondary)) return secondary;
  const exits = Object.keys(DIRECTIONS).filter((d) => d !== primary && d !== REVERSE[primary] && tryMove(grid, pos, d));
  return exits.length === 1 ? exits[0] : null;
}

/** Mur sur le côté `side` de la case (x, y), y compris vu depuis la case voisine ; hors grille : aucun. */
function wallAt(grid, x, y, side) {
  const size = grid.length;
  const inside = (cx, cy) => cx >= 0 && cy >= 0 && cx < size && cy < size;
  if (inside(x, y)) return grid[y][x].walls[side];
  const across = { top: [0, -1, 'bottom'], bottom: [0, 1, 'top'], left: [-1, 0, 'right'], right: [1, 0, 'left'] }[side];
  const [nx, ny, opposite] = [x + across[0], y + across[1], across[2]];
  return inside(nx, ny) ? grid[ny][nx].walls[opposite] : false;
}

/** Un mur touche-t-il le sommet (vx, vy) de la grille (coin commun à quatre cases) ? */
function wallAtVertex(grid, vx, vy) {
  return wallAt(grid, vx - 1, vy, 'top') || wallAt(grid, vx, vy, 'top')
    || wallAt(grid, vx, vy - 1, 'left') || wallAt(grid, vx, vy, 'left');
}

/** Repousse un point de rayon `radius` hors des murs de sa case et des bouts de murs voisins. */
function resolveCollision(grid, p, radius, axis) {
  const size = grid.length;
  const cx = Math.max(0, Math.min(size - 1, Math.floor(p.x)));
  const cy = Math.max(0, Math.min(size - 1, Math.floor(p.y)));
  let { x, y } = p;
  if (axis !== 'y') {
    if (wallAt(grid, cx, cy, 'left')) x = Math.max(x, cx + radius);
    if (wallAt(grid, cx, cy, 'right')) x = Math.min(x, cx + 1 - radius);
  }
  if (axis !== 'x') {
    if (wallAt(grid, cx, cy, 'top')) y = Math.max(y, cy + radius);
    if (wallAt(grid, cx, cy, 'bottom')) y = Math.min(y, cy + 1 - radius);
  }
  // Bouts de murs aux coins de la case : on contourne le coin au lieu de s'y accrocher
  for (const [vx, vy] of [[cx, cy], [cx + 1, cy], [cx, cy + 1], [cx + 1, cy + 1]]) {
    const dx = x - vx;
    const dy = y - vy;
    const dist = Math.hypot(dx, dy);
    if (dist >= radius || !wallAtVertex(grid, vx, vy)) continue;
    if (dist === 0) continue;
    x = vx + (dx / dist) * radius;
    y = vy + (dy / dist) * radius;
  }
  return { x, y };
}

/**
 * Souris traînée au doigt, en mouvement libre : elle va de `from` vers `to` (coordonnées
 * continues en cases : la case (x, y) couvre [x, x+1[ × [y, y+1[), sans jamais traverser un mur.
 * Contre un mur, elle glisse le long ; à un angle, elle contourne le bout du mur. Le trajet est
 * découpé en petits pas pour qu'aucun mur ne soit sauté.
 * @param {number} radius rayon du corps de la souris, en cases
 * @returns {{ pos: {x:number,y:number}, cells: {x:number,y:number}[] }} position atteinte, et les
 *   cases traversées dans l'ordre (hors case de départ) pour détecter le fromage au passage
 */
export function slideToward(grid, from, to, radius) {
  const cellOf = (p) => ({ x: Math.floor(p.x), y: Math.floor(p.y) });
  // Pas plus petit que le rayon : aucun mur ne peut être franchi d'un coup. Chaque pas part de la
  // position réellement atteinte (pas d'une interpolation du trajet), et chaque axe avance de lui-même :
  // bloqué sur un axe, on continue sur l'autre (glissement), et une ouverture rencontrée en chemin
  // est prise aussitôt si le doigt est de ce côté.
  const stepLen = radius / 2;
  const clampStep = (d) => Math.max(-stepLen, Math.min(stepLen, d));
  const maxSteps = Math.ceil(Math.max(Math.abs(to.x - from.x), Math.abs(to.y - from.y)) / stepLen) + 2;
  const cells = [];
  let pos = { ...from };
  let cell = cellOf(pos);
  for (let i = 0; i < maxSteps; i++) {
    const dx = to.x - pos.x;
    const dy = to.y - pos.y;
    if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9) break;
    const before = pos;
    pos = resolveCollision(grid, { x: pos.x + clampStep(dx), y: pos.y }, radius, 'x');
    pos = resolveCollision(grid, { x: pos.x, y: pos.y + clampStep(dy) }, radius, 'y');
    pos = resolveCollision(grid, pos, radius);
    if (Math.abs(pos.x - before.x) < 1e-9 && Math.abs(pos.y - before.y) < 1e-9) break; // bloqué
    const next = cellOf(pos);
    if (next.x !== cell.x || next.y !== cell.y) {
      cells.push(next);
      cell = next;
    }
  }
  return { pos, cells };
}
